<script setup>
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import PageHeader from '../PageHeader.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlEmptyState from '../xl/XlEmptyState.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import XlSearchField from '../xl/XlSearchField.vue';
import BrokerScopePicker from '../policyholders/BrokerScopePicker.vue';
import ReleasedBookNotice from '../policyholders/ReleasedBookNotice.vue';
import ConversationRow from '../messages/ConversationRow.vue';
import ConversationThread from '../messages/ConversationThread.vue';
import CallRoom from '../messages/CallRoom.vue';
import { callLeaveUrl, listConversations, reactTo, readConversation, sendMessage, startCall } from '../../api/messages';
import { listBrokers } from '../../api/policyholders';
import { useAuthStore } from '../../stores/auth';
import { useBrokerScopeStore } from '../../stores/brokerScope';
import { useEventStore } from '../../stores/event';
import { CAPABILITIES, can, canAssignBrokers } from '../../helpers/roles';
import { inboxEmptyStates } from '../../helpers/inboxEmptyState';
import { refreshHubBadges } from '../../composables/useHubBadges';
import { listenForMessages } from '../../composables/useMessagesLive';

// A fallback for when the live connection is down.
const POLL_MS = 60000;

const route = useRoute();
const router = useRouter();
const auth = useAuthStore();
const brokerScope = useBrokerScopeStore();
const eventStore = useEventStore();
const envURLs = inject('envURLs', {});

const canChoose = computed(() => canAssignBrokers(auth.userData));

const waiting = ref([]);
const answered = ref([]);
const answeredByHub = ref([]);
const bookSize = ref(null);
const brokers = ref([]);
const brokersLoaded = ref(false);
const invitationsPending = ref(0);
const search = ref('');
const loadingList = ref(true);
const listError = ref('');

// The open thread is the URL, so a conversation can be linked to and the back
// button steps out of it rather than off the page.
const openId = computed(() => (route.query.thread ? Number(route.query.thread) : null));

const thread = ref({ policyholder: null, messages: [], attachment_rules: undefined });
const loadingThread = ref(false);
const threadError = ref('');
const threadUnavailable = ref('');
// 404 is the answer for a policyholder outside this viewer's book.
const UNAVAILABLE_BY_STATUS = { 404: 'missing', 403: 'forbidden' };
const call = ref(null);

const applyList = (data) => {
  waiting.value = data.waiting;
  answered.value = data.answered;
  answeredByHub.value = data.answered_by_hub ?? [];
  bookSize.value = data.book_size ?? null;
  invitationsPending.value = data.invitations_pending ?? 0;
};

// Only the newest request may write: a slower, older answer to a different filter must not replace it.
let latestList = 0;
const ifLatest = (request, apply) => {
  if (request === latestList) apply();
};

const fetchList = async () => {
  const mine = ++latestList;
  listError.value = '';

  try {
    const data = await listConversations({
      ...(brokerScope.brokerId ? { broker_id: brokerScope.brokerId } : {}),
      ...(search.value.trim() ? { search: search.value.trim() } : {}),
    });
    ifLatest(mine, () => applyList(data));
  } catch {
    ifLatest(mine, () => { listError.value = 'Could not load your conversations.'; });
  } finally {
    ifLatest(mine, () => { loadingList.value = false; });
  }
};

const fetchThread = async (policyholderId) => {
  loadingThread.value = true;

  try {
    thread.value = await readConversation(policyholderId);
    threadUnavailable.value = '';
    threadError.value = '';
  } catch (e) {
    const state = UNAVAILABLE_BY_STATUS[e?.response?.status] ?? 'failed';
    if (state === 'failed' && (thread.value.policyholder || threadUnavailable.value)) return;

    threadUnavailable.value = state;
    thread.value = { policyholder: null, messages: [], attachment_rules: undefined };
  } finally {
    loadingThread.value = false;
  }
};

const refresh = () => (openId.value ? fetchThread(openId.value) : fetchList());

const onLiveChange = (policyholderId) => {
  if (!openId.value || policyholderId === null || policyholderId === openId.value) refresh();
};

const open = (policyholderId) => router.push({ query: { ...route.query, thread: policyholderId } });

const close = () => {
  const next = { ...route.query };
  delete next.thread;
  router.push({ query: next });
};

// A failed send rejects to the composer, which keeps the message and offers to retry it.
const send = async ({ body, files }) => {
  const policyholderId = openId.value;
  await sendMessage(policyholderId, body, files);
  await fetchThread(policyholderId);
  refreshHubBadges();
};

const escalate = async (mode) => {
  threadError.value = '';

  try {
    const message = await startCall(openId.value, mode);
    call.value = { room: message.call.room, host: true, leaveUrl: callLeaveUrl(openId.value, message.id) };
    fetchThread(openId.value);
  } catch {
    threadError.value = 'The call could not be started. Please try again.';
  }
};

const react = async ({ message, emoji }) => {
  try {
    message.reactions = await reactTo(openId.value, message.id, emoji);
  } catch {
    threadError.value = 'That reaction could not be saved. Please try again.';
  }
};

// A different thread starts blank, or the last one's header shows until this one loads.
const switchTo = (id) => {
  if (!id) return fetchList();
  thread.value = { policyholder: null, messages: [], attachment_rules: undefined };
  threadUnavailable.value = '';
  threadError.value = '';

  return fetchThread(id);
};

brokerScope.adoptFromRoute(route);

watch(openId, switchTo, { immediate: true });

watch(() => brokerScope.brokerId, fetchList);

let searchHandle = null;
watch(search, () => {
  clearTimeout(searchHandle);
  searchHandle = setTimeout(fetchList, 300);
});

const brokerName = computed(() => {
  const broker = brokers.value.find((b) => String(b.id) === String(brokerScope.brokerId));

  return broker ? [broker.first_name, broker.last_name].filter(Boolean).join(' ') || broker.email : '';
});

const hubName = computed(() => eventStore.event.value?.login_event_name || window.store?.subdomain || 'your hub');

const empty = computed(() => inboxEmptyStates({
  isBroker: !canChoose.value,
  waiting: waiting.value.length,
  answered: answered.value.length + answeredByHub.value.length,
  bookSize: bookSize.value,
  search: search.value.trim(),
  brokerFilter: brokerScope.brokerId,
  brokerName: brokerName.value,
  hubName: hubName.value,
  invitationsPending: invitationsPending.value,
  brokerCount: brokersLoaded.value ? brokers.value.length : null,
  canAddTeam: can(auth.userRole, CAPABILITIES.MANAGE_STAFF_ACCESS, auth.capabilities),
}));

const PAGE_ICONS = { 'empty-hub': 'users', 'none-assigned': 'mail' };

const ACTIONS = {
  invite: () => router.push({ path: '/policyholders', query: { tab: 'registered', create: 'invite' } }),
  team: () => window.open(`${envURLs.eco ?? ''}/team`, '_blank', 'noopener'),
  sessions: () => router.push('/sessions'),
  clear: () => {
    search.value = '';
    brokerScope.set('', router, route);
  },
};

let poll = null;
let stopListening = null;

onMounted(async () => {
  poll = setInterval(refresh, POLL_MS);
  stopListening = listenForMessages(onLiveChange);

  if (canChoose.value) {
    try {
      brokers.value = await listBrokers();
      brokersLoaded.value = true;
    } catch {
      // The picker simply offers fewer choices; the list still loads.
    }
  }
});

onBeforeUnmount(() => {
  clearInterval(poll);
  stopListening?.();
});
</script>

<template>
  <PageHeader />

  <div class="page-content" data-xl-gen="northstar">
    <div class="messages">
      <ConversationThread
        v-if="openId"
        :key="openId"
        :policyholder="thread.policyholder"
        :messages="thread.messages"
        :rules="thread.attachment_rules"
        :loading="loadingThread"
        :submit="send"
        :error="threadError"
        :unavailable="threadUnavailable"
        :show-broker="canChoose"
        :agency-name="hubName"
        @back="close"
        @retry="fetchThread(openId)"
        @home="router.push('/overview')"
        @call="escalate"
        @react="react"
        @join="(c) => (call = { room: c.room, host: false, leaveUrl: callLeaveUrl(openId, c.id) })"
      />

      <template v-else>
        <header class="messages__head">
          <h1>Messages</h1>
          <p>Private conversations with policyholders.</p>
        </header>

        <ReleasedBookNotice v-if="canChoose" :brokers="brokers" @settled="fetchList" />

        <div class="messages__controls">
          <span v-if="canChoose" class="messages__viewing">Viewing</span>
          <BrokerScopePicker
            :can-choose="canChoose"
            :brokers="brokers"
            :book-size="bookSize"
            :model-value="brokerScope.brokerId"
            @update:model-value="(value) => brokerScope.set(value, router, route)"
          />
        </div>

        <div class="messages__search">
          <XlSearchField v-model="search" placeholder="Search names and messages" aria-label="Search conversations by name or message" />
        </div>

        <p v-if="listError" role="alert" data-testid="messages-error">{{ listError }}</p>
        <!-- The list's own first heading's place, then its card: the rows used
             to land a heading lower than their skeleton ("Messages jumps"). -->
        <template v-else-if="loadingList">
          <div class="messages__section messages__section--first" aria-hidden="true">
            <XlSkeleton width="150px" height="0.85em" radius="6px" class="messages__section-sk" />
            <i />
          </div>
          <XlCard class="messages__list" data-testid="messages-loading" aria-busy="true">
            <div v-for="n in 3" :key="n" class="messages__skeleton">
              <XlSkeleton class="messages__skeleton-avatar" variant="circular" width="38px" />
              <div class="messages__skeleton-text"><XlSkeleton variant="text" :lines="2" /></div>
            </div>
          </XlCard>
        </template>

        <XlCard v-else-if="empty.page" data-testid="messages-empty">
          <XlEmptyState :headline="empty.page.headline" :body="empty.page.body" :icon="PAGE_ICONS[empty.page.key]" :data-state="empty.page.key">
            <XlButton
              v-for="action in empty.page.actions"
              :key="action.id"
              :variant="action.primary || empty.page.actions.length === 1 ? 'filled' : 'ghost'"
              :data-testid="`empty-${action.id}`"
              @click="ACTIONS[action.id]()"
            >{{ action.label }}</XlButton>
          </XlEmptyState>
        </XlCard>

        <template v-else>
          <h2 class="messages__section messages__section--first" data-testid="waiting-heading">
            <span>Waiting for {{ canChoose ? 'a reply' : 'you' }} · {{ waiting.length }}</span>
            <i aria-hidden="true" />
          </h2>
          <XlCard class="messages__list" data-testid="waiting-list">
            <ConversationRow
              v-for="conversation in waiting"
              :key="conversation.policyholder_id"
              :conversation="conversation"
              :show-broker="canChoose"
              @open="open"
            />
            <XlEmptyState v-if="empty.waiting" :headline="empty.waiting.headline" :body="empty.waiting.body" :data-state="empty.waiting.key" data-testid="waiting-empty">
              <XlButton
                v-for="action in empty.waiting.actions"
                :key="action.id"
                :data-testid="`empty-${action.id}`"
                @click="ACTIONS[action.id]()"
              >{{ action.label }}</XlButton>
            </XlEmptyState>
          </XlCard>

          <template v-if="answeredByHub.length">
            <h2 class="messages__section" data-testid="hub-heading">
              <span>Answered by the hub · {{ answeredByHub.length }}</span>
              <i aria-hidden="true" />
              <span class="messages__section-note">nothing to do — but you haven't read it</span>
            </h2>
            <XlCard class="messages__list" data-testid="hub-list">
              <ConversationRow
                v-for="conversation in answeredByHub"
                :key="conversation.policyholder_id"
                :conversation="conversation"
                :show-broker="canChoose"
                @open="open"
              />
            </XlCard>
          </template>

          <template v-if="answered.length || empty.answered">
            <h2 class="messages__section" data-testid="answered-heading">
              <span>Answered<template v-if="!canChoose"> · {{ answered.length }}</template></span>
              <i aria-hidden="true" />
            </h2>
            <XlCard class="messages__list" data-testid="answered-list">
              <ConversationRow
                v-for="conversation in answered"
                :key="conversation.policyholder_id"
                :conversation="conversation"
                :show-broker="canChoose"
                @open="open"
              />
              <XlEmptyState v-if="empty.answered" :headline="empty.answered.headline" :body="empty.answered.body" :data-state="empty.answered.key" data-testid="answered-empty" />
            </XlCard>
          </template>
        </template>
      </template>
    </div>
  </div>

  <CallRoom v-if="call" :room="call.room" :host="call.host" :leave-url="call.leaveUrl" @close="call = null; refresh()" />
</template>

<style scoped lang="scss">
.messages, .messages :deep(*) { box-sizing: border-box; }

.messages { font-family: var(--xl-font-family); color: var(--xl-color-primary-text); }

.messages__head { margin: 0; }

.messages__head h1 {
  margin: 0 0 6px;
  font-size: 30px;
  font-weight: var(--xl-font-weight-bold);
  letter-spacing: -0.03em;
  color: var(--xl-color-primary-text);
}

.messages__head p {
  margin: 0 0 22px;
  font-size: 15px;
  color: var(--xl-color-outline);
}

.messages__controls {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.messages__controls :deep([data-testid='broker-book']) { height: 30px; padding: 0 13px; }

.messages__viewing {
  font-size: 12.5px;
  color: var(--xl-color-outline);
}

.messages__search { margin-bottom: 22px; }

.messages__section {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 12px;
  margin: 34px 0 14px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--xl-color-outline-variant);

  &--first { margin-top: 0; }
}

.messages__section-note {
  margin-left: auto;
  text-transform: none;
  letter-spacing: 0;
  font-weight: 400;
  font-size: 12.5px;
}

.messages__section-sk { width: auto; flex: 0 0 auto; align-self: center; }

.messages__section i {
  flex: 1;
  height: 1px;
  background: var(--xl-color-border);
}

.messages__list { padding: 8px 22px; overflow: hidden; margin-bottom: 14px; }

.messages__skeleton {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 0;
}

.messages__skeleton-avatar { flex: none; width: 38px; }

.messages__skeleton-text { flex: 1; max-width: 420px; }
</style>
